import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RowsPerPageType } from '@node-space/ui-components'
import { ResultsLayout } from 'components/layout/ResultsLayout'
import { useSearchAndFilter } from 'components/SearchAndFilter/useSearchAndFilter'
import { useCustomersQuery } from 'hooks/queries/Customers/useCustomersQuery'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { EMBEDDED_CUSTOMER_STATUSES } from 'pages/EmbeddedCustomers/helpers/constants'
import { EmbeddedCustomerStatus } from 'pages/EmbeddedCustomers/helpers/types'
import { getCustomerStatusLabel, getCustomerTypeLabel } from 'pages/EmbeddedCustomers/helpers/utils'
import { StatusLabelMapping } from 'pages/NestedCustomers/constants'
import { Customer, CustomerEntityType, CustomersFilters, CustomerStatus } from 'types/customers'
import { AddCustomerCta } from '../AddCustomer/AddCustomerCta'
import { CustomerModel, CustomerModels } from '../helpers/types'
import { useCustomersQueryParams } from '../hooks/useCustomersQueryParams'
import { CustomerItems } from './CustomerItems'

const INITIAL_SEARCH = ''
const INITIAL_FILTERS: CustomersFilters = {}

export interface CustomersListProps {
  customerModel: CustomerModel
}

export const CustomersList = ({ customerModel }: CustomersListProps) => {
  const { t } = useTranslation()

  const {
    searchValue,
    appliedSearch,
    appliedFilters,
    hasSearchAndFilter,
    setSearchValue,
    onFilterChange,
    onResetAll,
  } = useSearchAndFilter(INITIAL_SEARCH, INITIAL_FILTERS)

  const { queryParams, onChangePage, onChangeRowsPerPage, onChangeSearchAndFilter } =
    useCustomersQueryParams(customerModel)

  const {
    data: { content: customersData = [], pagination } = {},
    isFetching,
    isError,
    refetch: refetchCustomers,
  } = useCustomersQuery<Customer>(queryParams)

  const { paging, showPagination } = usePaginationPagingPersist(pagination)

  const hasPagination = useMemo(() => !!paging && !showPagination, [paging, showPagination])
  const isEmbedded = useMemo(() => customerModel === CustomerModels.EMBEDDED, [customerModel])
  const statusFilters = useMemo(
    () => Object.values(isEmbedded ? EMBEDDED_CUSTOMER_STATUSES : CustomerStatus),
    [isEmbedded]
  )

  const getStatusLabel = (status: string) => {
    return isEmbedded
      ? getCustomerStatusLabel(status as EmbeddedCustomerStatus, t)
      : StatusLabelMapping?.[status]
  }

  const AddButton = (
    <AddCustomerCta customerModel={customerModel} refetchCustomers={refetchCustomers} />
  )

  useEffect(() => {
    onChangeSearchAndFilter({ search: appliedSearch })
  }, [appliedSearch])

  useEffect(() => {
    onChangeSearchAndFilter({
      filters: { models: [customerModel], ...(appliedFilters ?? {}) },
    })
  }, [appliedFilters])

  return (
    <ResultsLayout
      testId="customers-results"
      isLoading={isFetching}
      isError={isError}
      hasResults={!!customersData?.length}
      hasSearchAndFilter={hasSearchAndFilter}
      refetchResults={refetchCustomers}
      resetSearchAndFilters={onResetAll}
      emptyState={{
        title: t('embeddedCustomers.emptyStatus.headline'),
        description: isEmbedded
          ? t('embeddedCustomers.emptyStatus.descriptionAddDetails')
          : t('customers.addNewCustomerDescription'),
        PrimaryButton: AddButton,
      }}
      header={{
        description: isEmbedded ? t('embeddedCustomers.description') : t('customers.description'),
        renderRightSection: AddButton,
      }}
      searchAndFilter={{
        search: {
          placeholder: t('customers.searchPlaceholder'),
          value: searchValue,
          onChange: setSearchValue,
        },
        filters: [
          {
            id: 'statuses',
            placeholder: t('status'),
            values: statusFilters,
            checkedValues: appliedFilters?.statuses,
            getLabel: getStatusLabel,
            onFilterChange,
          },
          {
            id: 'types',
            placeholder: t('type'),
            values: Object.values(CustomerEntityType),
            checkedValues: appliedFilters?.types,
            getLabel: (type: CustomerEntityType) => getCustomerTypeLabel(type, t),
            onFilterChange,
          },
        ],
        onResetAll,
      }}
      {...(hasPagination && {
        pagination: {
          page: queryParams?.page,
          total: paging?.total,
          rowsPerPage: queryParams?.size as RowsPerPageType,
          onChangePage,
          onChangeRowsPerPage,
        },
      })}
    >
      <CustomerItems customerModel={customerModel} customers={customersData} />
    </ResultsLayout>
  )
}
